import {
    IUsesSummary, IProFormaUses, ISourcesSummary, IWaterfallAnalysis,
    IProFormaReturnsData,
    IIncomeSummary,
    IOperatingAndPermanentDebtInfo,
    IExpenseSummary,
    IDispositionData,
} from 'views/ProFormaTable/types';
import { WattsType } from 'views/ProFormaTable/constants';
import { numeralFormatter, numeralFormatterCurrency } from 'ns_libs/formatter';
import { ProFormaModelIds, IncomeCalculationType } from 'constants/proForma';
import { Section, Format, Row, RowValue } from './ExecutiveSummaryGenericTable/types';

export function formatDevelopment(usesSummary: IUsesSummary | undefined, sourcesSummary: ISourcesSummary | undefined): Section[] {
    const sections: Section[] = [];

    if (usesSummary?.uses) {
        const totalUses = usesSummary.totals.amount || 0;
        const usesMap = new Map<number | 'root', IProFormaUses[]>();
        usesSummary.uses.forEach(use => {
            const parentId = use.parent?.id || 'root';
            if (!usesMap.has(parentId)) {
                usesMap.set(parentId, []);
            }
            usesMap.get(parentId)?.push(use);
        });

        usesMap.forEach((uses, parentId) => {
            const section: Section = {
                id: `uses-section-${parentId}`,
                title: parentId === 'root' ? 'Uses' : uses[0]?.parent?.nodeName || '',
                columns: ['%', '$ / Built SF', '$ / Unit', 'Amount'],
                rows: uses.map(use => ({
                    id: `use-${use.id}`,
                    label: use.description || '',
                    values: [
                        {
                            value: totalUses !== 0 && use.amount ? numeralFormatter((use.amount / totalUses), '0,0.00') : '-',
                            format: 'percentage',
                        },
                        {
                            value: numeralFormatterCurrency(use.costPerGrossSquareFootage || '-', '$0,0.00'),
                            format: 'currency',
                        },
                        {
                            value: numeralFormatterCurrency(use.costPerResidentialUnit || '-', '$0,0.00'),
                            format: 'currency',
                        },
                        {
                            value: use.amount || '-',
                            format: 'currency',
                        },
                    ],
                })),
            };
            sections.push(section);
        });

        if (usesSummary.totals) {
            sections.push({
                id: 'uses-total',
                isTotal: true,
                rows: [{
                    id: 'total-uses',
                    label: 'Total uses',
                    values: [
                        { value: 1, format: 'percentage' },
                        {
                            value: numeralFormatterCurrency(usesSummary.totals.costPerGrossSquareFootage || '-', '$0,0.00'),
                            format: 'currency',
                        },
                        {
                            value: numeralFormatterCurrency(usesSummary.totals.costPerResidentialUnit || '-', '$0,0.00'),
                            format: 'currency',
                        },
                        {
                            value: usesSummary.totals.amount || '-',
                            format: 'currency',
                        },
                    ],
                }],
            });
        }
    }

    if (sourcesSummary?.sources) {
        sections.push({
            id: 'sources-section',
            title: 'Sources',
            columns: [], // Empty array to hide column headers
            rows: sourcesSummary.sources.map(source => ({
                id: `source-${source.id}`,
                label: source.name,
                values: [
                    { value: source.sourcesPercentage || '-', format: 'percentage' },
                    { value: numeralFormatterCurrency(source.costPerGrossSquareFootage || '-', '$0,0.00'), format: 'currency' },
                    { value: numeralFormatterCurrency(source.costPerResidentialUnit || '-', '$0,0.00'), format: 'currency' },
                    { value: source.amount || '-', format: 'currency' },
                ],
            })),
        });

        if (sourcesSummary.totals) {
            sections.push({
                id: 'sources-total',
                isTotal: true,
                rows: [{
                    id: 'total-sources',
                    label: 'Total sources',
                    values: [
                        { value: 1, format: 'percentage' },
                        { value: numeralFormatterCurrency(sourcesSummary.totals.costPerGrossSquareFootage || '-', '$0,0.00'), format: 'currency' },
                        { value: numeralFormatterCurrency(sourcesSummary.totals.costPerResidentialUnit || '-', '$0,0.00'), format: 'currency' },
                        { value: sourcesSummary.totals.amount || '-', format: 'currency' },
                    ],
                }],
            });
        }
    }

    return sections;
}

export function formatAssumptions(
    proFormaModelId: number,
    incomeSummary: IIncomeSummary,
    operatingInfo: IOperatingAndPermanentDebtInfo,
    expenses: IExpenseSummary,
    wattsType: WattsType,
): Section[] {
    const totalValuePerSfPerYear = incomeSummary?.totalRentalIncome?.totalValuePerSfPerYear;
    const perSf = totalValuePerSfPerYear ? totalValuePerSfPerYear / 12 : '-';
    const perUnit = incomeSummary?.totalRentalIncome?.totalPricePerUnit ?? '-';
    const rentalIncomeGrowth = operatingInfo?.rentalGrowthPercentage ?? '-';
    const expensesGrowth = expenses?.totalOperatingExpenses?.totalAnnualGrowth ?? '-';

    const isApartment = ProFormaModelIds.APARTMENT === proFormaModelId;
    const isApartmentWithRetail = ProFormaModelIds.APARTMENT_WITH_RETAIL === proFormaModelId;
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || isApartmentWithRetail || isIncomeCalculationTypeEstimate ? 'SF' : 'RSF';

    let generalVacancy = '-';
    let creditLoss = '-';
    if (Array.isArray(incomeSummary?.incomeAdjustments)) {
        incomeSummary.incomeAdjustments.forEach((adj: any) => {
            if (adj.adjustmentType === 'General Vacancy') {
                generalVacancy = adj.percentOfIncome ?? '-';
            }
            if (adj.adjustmentType === 'Credit Loss') {
                creditLoss = adj.percentOfIncome ?? '-';
            }
        });
    }

    let effectiveRentRows: Row[] = [];

    if (isApartment) {
        effectiveRentRows = [
            { id: 'per-rsf', label: 'Per $/RSF/mo', values: [{ value: perSf, format: 'currency' }] },
            { id: 'per-unit', label: 'Per $/unit/mo', values: [{ value: perUnit, format: 'currency' }] },
        ];
    } else if (isApartmentWithRetail) {
        const retailPerSf = incomeSummary?.totalRetailIncome?.totalValuePerSfPerYear
            ? incomeSummary.totalRetailIncome.totalValuePerSfPerYear / 12
            : '-';

        effectiveRentRows = [
            { id: 'per-rsf', label: 'Apartment $/RSF/mo', values: [{ value: perSf, format: 'currency' }] },
            { id: 'retail-per-sf', label: 'Retail $/RSF/mo', values: [{ value: retailPerSf, format: 'currency' }] },
            { id: 'per-unit', label: 'Apartment $/unit/mo', values: [{ value: perUnit, format: 'currency' }] },
        ];
    } else if ([ProFormaModelIds.OFFICE, ProFormaModelIds.RETAIL, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId)) {
        effectiveRentRows = [
            { id: 'per-rsf', label: '$/RSF/mo', values: [{ value: perSf, format: 'currency' }] },
        ];
    } else if (proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        const perKw = incomeSummary?.totalRentalIncome?.totalValuePerKwPerMonth ?? '-';
        const perMw = incomeSummary?.totalRentalIncome?.totalValuePerMwPerMonth ?? '-';
        effectiveRentRows = [
            { id: 'per-sf', label: `$/${areaType}/mo`, values: [{ value: perSf, format: 'currency' }] },
            wattsType === WattsType.MEGAWATTS
                ? { id: 'per-mw', label: '$/MW/mo', values: [{ value: perMw, format: 'currency' }] }
                : { id: 'per-kw', label: '$/kW/mo', values: [{ value: perKw, format: 'currency' }] },
        ];
    }

    return [
        {
            id: 'effective-rent-section',
            title: 'Effective rent',
            columns: [''],
            rows: effectiveRentRows,
        },
        {
            id: 'annual-growth-rate-section',
            title: 'Annual growth rate',
            columns: [''],
            rows: [
                { id: 'rental-income', label: 'Rental income', values: [{ value: rentalIncomeGrowth, format: 'percentage' }] },
                { id: 'expenses', label: 'Expenses', values: [{ value: expensesGrowth, format: 'percentage' }] },
            ],
        },
        {
            id: 'income-adjustments-section',
            title: 'Income adjustments',
            columns: [''],
            rows: [
                { id: 'general-vacancy', label: 'General vacancy', values: [{ value: generalVacancy, format: 'percentage' }] },
                { id: 'credit-loss', label: 'Credit loss', values: [{ value: creditLoss, format: 'percentage' }] },
            ],
        },
    ];
}

export function formatForecast(
    disposition: IDispositionData,
    operatingInfo: IOperatingAndPermanentDebtInfo,
    returnsData: IProFormaReturnsData,
): Section[] {
    const netSaleProceeds = disposition?.netSaleProceeds?.amount ?? '-';
    const permanentLoanPayoff = operatingInfo?.loanPayoffAmount ?? '-';
    const equityProceeds = returnsData?.totalEquityProceedFromCapitalEvents ?? '-';

    const cashOnCashReturn = operatingInfo?.cashOnCashReturn ?? '-';
    const debtServiceCoverageRatio = operatingInfo?.debtServiceCoverageRatio
        ? `${numeralFormatter(operatingInfo.debtServiceCoverageRatio, '0,0.00')}x`
        : '-';

    const operatingExpenseRatio = operatingInfo?.operatingExpenseRatio ?? '-';
    const breakevenOccupancy = returnsData?.breakevenOccupancy ?? '-';

    return [
        {
            id: 'forecast-section-1',
            columns: ['Value'],
            rows: [
                { id: 'net-sale-proceeds', label: 'Net sale proceeds', values: [{ value: netSaleProceeds, format: 'currency' }] },
                {
                    id: 'permanent-loan-payoff',
                    label: 'Permanent loan payoff',
                    values: [{ value: permanentLoanPayoff, format: 'currency', isNegative: true }],
                },
                {
                    id: 'equity-proceeds',
                    label: 'Equity proceeds from sale',
                    values: [
                        { value: equityProceeds, format: 'currency' },
                    ],
                },
            ],
        },
        {
            id: 'forecast-section-2',
            columns: ['Value'],
            rows: [
                { id: 'cash-on-cash-return', label: 'Cash-on-cash return', values: [{ value: cashOnCashReturn, format: 'percentage' }] },
                { id: 'debt-service-coverage-ratio', label: 'Debt service coverage ratio', values: [{ value: debtServiceCoverageRatio, format: 'none' }] },
            ],
        },
        {
            id: 'forecast-section-3',
            columns: ['Value'],
            rows: [
                { id: 'operating-expense-ratio', label: 'Operating expense ratio', values: [{ value: operatingExpenseRatio, format: 'percentage' }] },
                { id: 'breakeven-occupancy', label: 'Breakeven occupancy', values: [{ value: breakevenOccupancy, format: 'percentage' }] },
            ],
        },
    ];
}

export function formatReturns(returnsData: IProFormaReturnsData, waterfallAnalysis: IWaterfallAnalysis): Section[] {
    if (!returnsData || Object.keys(returnsData).length === 0) {
        return [];
    }

    const unleveredIrr = returnsData?.netUnleveredIrr ?? '-';
    const unleveredEmx = returnsData?.netUnleveredEquityMultiple ?? '-';
    const unleveredRoi = returnsData?.unleveredRoi ?? '-';
    const leveredIrr = returnsData?.netLeveredIrr ?? '-';
    const leveredEmx = returnsData?.netLeveredEquityMultiple ?? '-';
    const leveredNetRoi = returnsData?.leveredRoi ?? '-';

    const partnershipRows = [
        {
            id: 'lp',
            label: waterfallAnalysis?.waterfallSummary?.lpName || 'Limited Partner',
            values: [
                { value: waterfallAnalysis?.waterfallSummary?.lpNetProfit, format: 'currency' as Format },
                { value: waterfallAnalysis?.waterfallSummary?.lpRoi, format: 'percentage' as Format },
                { value: numeralFormatter(waterfallAnalysis?.waterfallSummary?.lpEmx, '0,0.00'), format: 'none' as Format, label: 'x' },
                { value: waterfallAnalysis?.waterfallSummary?.lpIrr, format: 'percentage' as Format },
            ],
        },
        {
            id: 'gp1',
            label: waterfallAnalysis?.waterfallSummary?.gp1Name || 'General Partner',
            values: [
                { value: waterfallAnalysis?.waterfallSummary?.gp1NetProfit, format: 'currency' as Format },
                { value: waterfallAnalysis?.waterfallSummary?.gp1Roi, format: 'percentage' as Format },
                { value: numeralFormatter(waterfallAnalysis?.waterfallSummary?.gp1Emx, '0,0.00'), format: 'none' as Format, label: 'x' },
                { value: waterfallAnalysis?.waterfallSummary?.gp1Irr, format: 'percentage' as Format },
            ],
        },
    ];

    const yieldOnCost = returnsData?.yieldOnCost ?? {};
    const marketCapRate = returnsData?.marketCapitalizationRate ?? {};
    const developmentSpread = returnsData?.developmentSpread ?? {};

    return [
        {
            id: 'property-returns-section',
            title: 'Property returns',
            columns: ['', 'ROI', 'EMx', 'IRR'],
            rows: [
                {
                    id: 'unlevered-row',
                    label: 'Unlevered',
                    values: [
                        { value: '' },
                        { value: unleveredRoi, format: 'percentage' },
                        { value: numeralFormatter(unleveredEmx, '0,0.00'), format: 'none', label: 'x' },
                        { value: unleveredIrr, format: 'percentage' },
                    ],
                },
                {
                    id: 'levered-row',
                    label: 'Levered',
                    values: [
                        { value: '' },
                        { value: leveredNetRoi, format: 'percentage' },
                        { value: numeralFormatter(leveredEmx, '0,0.00'), format: 'none', label: 'x' },
                        { value: leveredIrr, format: 'percentage' },
                    ],
                },
            ],
        },
        {
            id: 'partnership-returns-section',
            title: 'Partnership returns',
            columns: ['Net proceeds', 'ROI', 'EMx', 'IRR'],
            rows: partnershipRows,
        },
        {
            id: 'development-returns-section',
            title: 'Development returns',
            columns: ['', 'Untrended', 'Trended', 'Sale'],
            rows: [
                {
                    id: 'yield-on-cost-row',
                    label: 'Yield-on-cost',
                    values: [
                        { value: '', format: 'percentage' },
                        { value: yieldOnCost?.untrended ?? '-', format: 'percentage' },
                        { value: yieldOnCost?.trended ?? '-', format: 'percentage' },
                        { value: yieldOnCost?.sale ?? '-', format: 'percentage' },
                    ],
                },
                {
                    id: 'market-cap-rate-row',
                    label: 'Market cap rate',
                    values: [
                        { value: '', format: 'percentage' },
                        { value: marketCapRate?.current ?? '-', format: 'percentage' },
                        { value: marketCapRate?.trended ?? '-', format: 'percentage' },
                        { value: marketCapRate?.onSale ?? '-', format: 'percentage' },
                    ],
                },
                {
                    id: 'development-spread-row',
                    label: 'Development spread',
                    values: [
                        { value: '', format: 'percentage' },
                        { value: numeralFormatter(developmentSpread?.untrended ?? '-', '0,0.00'), format: 'none', label: ' bps' },
                        { value: numeralFormatter(developmentSpread?.trended ?? '-', '0,0.00'), format: 'none', label: ' bps' },
                        { value: numeralFormatter(developmentSpread?.sale ?? '-', '0,0.00'), format: 'none', label: ' bps' },
                    ],
                },
            ],
        },
    ];
}

export function formatOperations(
    proFormaModelId: number,
    disposition: IDispositionData,
    incomeCalculationType: IncomeCalculationType,
    wattsType: WattsType,
): Section[] {
    if (!disposition) return [];

    return [
        formatCashFlows(disposition),
        formatNetOperatingIncome(disposition),
        formatCashFlowFromOperations(proFormaModelId, disposition, incomeCalculationType, wattsType),
    ];
}

function formatCashFlows(disposition: IDispositionData): Section {
    return {
        id: 'cash-flows-section',
        title: 'Cash flows',
        columns: ['Untrended', 'Trended', 'Sale'],
        rows: [
            {
                id: 'effective-gross-revenue',
                label: 'Effective gross revenue',
                values: [
                    { value: disposition.totalEffectiveGrossRevenue?.untrended, format: 'currency' },
                    { value: disposition.totalEffectiveGrossRevenue?.trended, format: 'currency' },
                    { value: disposition.totalEffectiveGrossRevenue?.sale, format: 'currency' },
                ],
            },
            {
                id: 'operating-expenses',
                label: 'Operating expenses',
                values: [
                    { value: disposition.totalOperatingExpenses?.untrended, format: 'currency', isNegative: true },
                    { value: disposition.totalOperatingExpenses?.trended, format: 'currency', isNegative: true },
                    { value: disposition.totalOperatingExpenses?.sale, format: 'currency', isNegative: true },
                ],
            },
        ],
    };
}

function formatNetOperatingIncome(disposition: IDispositionData): Section {
    return {
        id: 'net-operating-income-section',
        rows: [
            {
                id: 'net-operating-income',
                label: 'Net operating income',
                values: [
                    { value: disposition.netOperatingIncome?.untrended, format: 'currency' },
                    { value: disposition.netOperatingIncome?.trended, format: 'currency' },
                    { value: disposition.netOperatingIncome?.sale, format: 'currency' },
                ],
            },
            {
                id: 'capital-expenditures',
                label: 'Capital expenditures',
                values: [
                    { value: disposition.totalCapitalExpenditures?.untrended, format: 'currency', isNegative: true },
                    { value: disposition.totalCapitalExpenditures?.trended, format: 'currency', isNegative: true },
                    { value: disposition.totalCapitalExpenditures?.sale, format: 'currency', isNegative: true },
                ],
            },
        ],
    };
}

function formatCashFlowFromOperations(
    proFormaModelId: number,
    disposition: IDispositionData,
    incomeCalculationType: IncomeCalculationType,
    wattsType: WattsType,
): Section {
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId);
    const isIncomeCalculationTypeEstimate = incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || isIncomeCalculationTypeEstimate ? 'SF' : 'RSF';

    let valuationPerUnitRows: Row[] = [];

    const valuationValuesPerUnit: RowValue[] = [
        { value: disposition.stabilizedValuePerUnit?.untrended, format: 'currency' },
        { value: disposition.stabilizedValuePerUnit?.trended, format: 'currency' },
        { value: disposition.stabilizedValuePerUnit?.sale, format: 'currency' },
    ];

    const valuationValuesPerSf: RowValue[] = [
        { value: disposition.stabilizedValuePerSf?.untrended, format: 'currency' },
        { value: disposition.stabilizedValuePerSf?.trended, format: 'currency' },
        { value: disposition.stabilizedValuePerSf?.sale, format: 'currency' },
    ];

    if (proFormaModelId === ProFormaModelIds.APARTMENT) {
        valuationPerUnitRows = [{
            id: 'valuation-unit',
            label: 'Valuation / unit',
            values: valuationValuesPerUnit,
            className: 'pl-2',
        }];
    } else if (proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL) {
        valuationPerUnitRows = [
            {
                id: 'valuation-unit',
                label: 'Valuation / unit',
                values: valuationValuesPerUnit,
                className: 'pl-2',
            },
            {
                id: 'valuation-sf',
                label: 'Valuation / SF',
                values: valuationValuesPerSf,
                className: 'pl-2',
            },
        ];
    } else if ([ProFormaModelIds.OFFICE, ProFormaModelIds.RETAIL, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId)) {
        valuationPerUnitRows = [{
            id: 'valuation-sf',
            label: `Valuation / ${areaType}`,
            values: valuationValuesPerSf,
            className: 'pl-2',
        }];
    } else if (proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        if (wattsType === WattsType.MEGAWATTS) {
            valuationPerUnitRows = [{
                id: 'valuation-mw',
                label: 'Valuation / MW',
                values: [
                    { value: disposition.stabilizedValuePerMw?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValuePerMw?.trended, format: 'currency' },
                    { value: disposition.stabilizedValuePerMw?.sale, format: 'currency' },
                ],
                className: 'pl-2',
            }];
        } else {
            valuationPerUnitRows = [{
                id: 'valuation-kw',
                label: 'Valuation / kW',
                values: [
                    { value: disposition.stabilizedValuePerKw?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValuePerKw?.trended, format: 'currency' },
                    { value: disposition.stabilizedValuePerKw?.sale, format: 'currency' },
                ],
                className: 'pl-2',
            }];
        }
    }

    return {
        id: 'cash-flow-from-operations-section',
        rows: [
            {
                id: 'cash-flow-operations',
                label: 'Cash flow from operations',
                values: [
                    { value: disposition.cashFlowFromOperations?.untrended, format: 'currency' },
                    { value: disposition.cashFlowFromOperations?.trended, format: 'currency' },
                    { value: disposition.cashFlowFromOperations?.sale, format: 'currency' },
                ],
            },
            {
                id: 'cap-rate',
                label: 'Cap rate',
                values: [
                    { value: disposition.marketCapitalizationRate?.current, format: 'percentage' },
                    { value: disposition.marketCapitalizationRate?.trended, format: 'percentage' },
                    { value: disposition.marketCapitalizationRate?.onSale, format: 'percentage' },
                ],
            },
            {
                id: 'valuation',
                label: 'Valuation',
                values: [
                    { value: disposition.stabilizedValue?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValue?.trended, format: 'currency' },
                    { value: disposition.stabilizedValue?.sale, format: 'currency' },
                ],
            },
            {
                id: 'percent-of-total-project-cost',
                label: '% of total project cost',
                values: [
                    { value: disposition.percentOfTotalProjectCost?.untrended, format: 'percentage' },
                    { value: disposition.percentOfTotalProjectCost?.trended, format: 'percentage' },
                    { value: disposition.percentOfTotalProjectCost?.sale, format: 'percentage' },
                ],
                className: 'pl-2',
            },
            ...valuationPerUnitRows,
        ],
    };
}
